import React, { ReactNode, useState } from 'react';
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { updateQuizTranslations } from 'store/quiz/quizSlice';
import { selectQuiz } from 'store/quiz/quizSelectors';
// MUI
import { MenuItem, TextField, Tooltip } from '@mui/material';
import { makeStyles } from '@mui/styles';
// i18next
import { useTranslation } from 'react-i18next';
// Utilities
import { isLocalStorageAvailable } from 'utilities/Utilities';

export const countries = [
  { label: 'English', value: 'en', imgUrl: '/images/flags/us.png' },
  { label: 'Español', value: 'es', imgUrl: '/images/flags/es.png' },
  { label: 'Svenska', value: 'se', imgUrl: '/images/flags/se.png' },
  { label: 'Português', value: 'pt', imgUrl: '/images/flags/pt.png' },
  { label: 'العربية', value: 'ar', imgUrl: '/images/flags/ar.png' },
  { label: 'हिन्दी', value: 'hi', imgUrl: '/images/flags/hi.png' },
  { label: 'Gaeilge', value: 'ga', imgUrl: '/images/flags/ga.png' },
  { label: 'Deutsch', value: 'de', imgUrl: '/images/flags/de.png' },
  { label: 'Italiano', value: 'it', imgUrl: '/images/flags/it.png' },
  { label: 'Français', value: 'fr', imgUrl: '/images/flags/fr.png' },
]

const LanguageSelect:React.FC = () => {
  const classes = useStyles();

  const dispatch = useAppDispatch();

  const quiz = useAppSelector(selectQuiz);

  const nextCountries = countries.filter(country => quiz.additionalLanguages?.includes(country.value) || quiz.language === country.value);

  const { t, i18n } = useTranslation();

  const [lang, setLang] = useState(() => {
    if (isLocalStorageAvailable()) {
      return localStorage.getItem('i18nextLng') || 'en';
    }
    return 'en';
  });

  const handleLanguage = (event:any) => {
    const { value:lang } = event.target;
    setLang(lang);
    i18n.changeLanguage(lang);
    dispatch(updateQuizTranslations(lang));
  }

  if (nextCountries.length <= 1) return null;
  return (
    <div className="mb-2 w-36 mx-auto">
      <TextField
        fullWidth
        className={classes.root}
        variant="outlined"
        size="small"
        select
        onChange={handleLanguage}
        value={lang}
        SelectProps={{
          renderValue: (value: any):ReactNode => {
            const country = countries.find(country => country.value === value);
            return (
              <Tooltip title={t('labels.chooseLanguage')} placement="top" arrow>
                <div className="flex items-center justify-between gap-2">
                  <p>{country?.label}</p>
                  <img src={country?.imgUrl} width="24" height="24" style={{ objectFit: 'cover', borderRadius: '100%' }} alt={lang} />
                </div>
              </Tooltip>
            )
          }
        }}
        InputProps={{
          style: {
            color: '#282728',
            fontSize: '14px',
            fontWeight: 600
          }
        }}
      >
        {nextCountries.map(country => (
          <MenuItem key={country.value} value={country.value}>
            <div className="w-full flex items-center justify-between gap-4">
              <p>{country.label}</p>
              <img src={country.imgUrl} width="24" height="24" style={{ objectFit: 'cover', borderRadius: '100%' }} alt={lang} />
            </div>
          </MenuItem>
        ))}
      </TextField>
    </div>
  );
}

export default LanguageSelect;

const useStyles = makeStyles({
  root: {
    '& label': {
      color: '#222',
    },
    '& label.Mui-focused': {
      color: '#222',
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: '20px',
      backgroundColor: '#fefefe',
      '& .MuiSelect-select': {
        padding: '4px 4px 4px 8px !important',
      },
      '& fieldset': {
        borderColor: '#fefefe',
        borderRadius: '20px'
      },
      '&:hover fieldset': {
        borderColor: '#fefefe',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#fefefe',
      }
    },
    '& .MuiOutlinedInput-root svg': {
      display: 'none'
    }
  }
});
